import { Spinner } from '@nextui-org/spinner';
import React, { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { TypographyUI } from '@portal/components/common/Typography.ui';
import { useAuthStatus } from '@portal/utils';
import { useEncryptionStatus } from '@portal/utils/useEncryptionStatus';

interface AuthGuardProps {
  children: ReactNode;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
}

// System state mapping
type SystemState =
  | 'encryptionNotReady'
  | 'missingKeyFileWithData'
  | 'systemChanged'
  | 'encryptionLoading'
  | 'authNotReady'
  | 'authLoading'
  | 'authError'
  | 'backendError'
  | 'ready';

// Expected pages for each state
const STATE_PAGE_MAPPING: Record<SystemState, string> = {
  encryptionNotReady: '/encryption/status',
  missingKeyFileWithData: '/encryption/status',
  systemChanged: '/encryption/status',
  encryptionLoading: '*', // Show encryption page while loading
  authNotReady: '/login',
  authLoading: '/login', // Show login page while loading
  authError: '/login',
  backendError: '/login', // Fallback to login for backend errors
  ready: '*', // Allow any page except login when ready
};

export const AuthGuard = ({ children, requireAdmin = false, requireSuperAdmin = false }: AuthGuardProps) => {
  const navigate = useNavigate();
  const { user, isAdmin, isSuperAdmin, isLoading, isError, error } = useAuthStatus();
  const { encryptionStatus, isLoading: encryptionLoading, isEncryptionReady } = useEncryptionStatus();

  // Determine current system state
  const getCurrentState = (): SystemState => {
    // User is authenticated, now check encryption status
    if (encryptionLoading) {
      // If user is authenticated and encryption is loading, allow access to main app
      return 'encryptionLoading';
    }

    // Check for system changes first
    if (encryptionStatus?.status === 'system_changed') {
      return 'systemChanged';
    }

    // Check for missing key file with encrypted data
    if (encryptionStatus?.status === 'missing_with_data') {
      return 'missingKeyFileWithData';
    }

    if (!isEncryptionReady) {
      return 'encryptionNotReady';
    }

    // Check authentication first
    if (isLoading) {
      return 'authLoading';
    }

    if (isError) {
      if (error?.status === 401) {
        return 'authError';
      }
      return 'backendError';
    }

    const hasToken = localStorage.getItem('token');
    if (!hasToken) {
      return 'authNotReady';
    }

    // All checks passed
    return 'ready';
  };

  // Check if current page matches expected page for current state
  const shouldRedirect = (): { shouldRedirect: boolean; targetPage: string } => {
    const currentState = getCurrentState();
    const expectedPage = STATE_PAGE_MAPPING[currentState];
    const currentPath = window.location.pathname;

    // Handle ready state with '*' - allow any page except login
    if (expectedPage === '*') {
      // If user is on login page, redirect to monitors
      if (currentPath === '/login') {
        return { shouldRedirect: true, targetPage: '/monitors' };
      }
      // If user is on root page, redirect to monitors
      if (currentPath === '/') {
        return { shouldRedirect: true, targetPage: '/monitors' };
      }
      // For any other page, allow user to stay
      return { shouldRedirect: false, targetPage: expectedPage };
    }

    // Check if current page matches expected page
    if (currentPath !== expectedPage) {
      // Special case: if encryption setup/recovery is in progress, don't redirect away from encryption page
      const isSetupInProgress = localStorage.getItem('encryption_setup_in_progress') === 'true';
      const isRecoveryInProgress = localStorage.getItem('encryption_recovery_in_progress') === 'true';
      if ((isSetupInProgress || isRecoveryInProgress) && currentPath.startsWith('/encryption')) {
        return { shouldRedirect: false, targetPage: expectedPage };
      }

      return { shouldRedirect: true, targetPage: expectedPage };
    }

    return { shouldRedirect: false, targetPage: expectedPage };
  };

  // Handle redirection based on state
  useEffect(() => {
    const { shouldRedirect: needsRedirect, targetPage } = shouldRedirect();
    if (needsRedirect) {
      navigate(targetPage, { replace: true });
    }
  }, [user, isLoading, isError, error?.status, encryptionStatus, encryptionLoading, isEncryptionReady, navigate]);

  // Show loading state
  const currentState = getCurrentState();
  if (currentState === 'encryptionLoading' || currentState === 'authLoading') {
    return (
      <div className='flex w-full h-screen gap-4 items-center justify-center'>
        <div className='flex items-center gap-8'>
          <Spinner />
          <TypographyUI variant='h5' color='blue-gray'>
            {currentState === 'encryptionLoading' ? 'Checking system status...' : 'Checking authentication...'}
          </TypographyUI>
        </div>
      </div>
    );
  }

  // Show error state for backend errors
  if (currentState === 'backendError') {
    return (
      <div className='flex w-full h-screen gap-4 items-center justify-center'>
        <div className='flex items-center gap-8'>
          <TypographyUI variant='h5' color='blue-gray'>
            Backend is down
          </TypographyUI>
        </div>
      </div>
    );
  }

  // Check admin requirements (only for authenticated users)
  if (currentState === 'ready') {
    const hasToken = localStorage.getItem('token');

    if (hasToken && user) {
      if (requireSuperAdmin && !isSuperAdmin) {
        return (
          <div className='flex w-full h-screen gap-4 items-center justify-center'>
            <div className='flex items-center gap-8'>
              <TypographyUI variant='h5' color='blue-gray'>
                Super admin access required
              </TypographyUI>
            </div>
          </div>
        );
      }

      if (requireAdmin && !isAdmin && !isSuperAdmin) {
        return (
          <div className='flex w-full h-screen gap-4 items-center justify-center'>
            <div className='flex items-center gap-8'>
              <TypographyUI variant='h5' color='blue-gray'>
                Admin access required
              </TypographyUI>
            </div>
          </div>
        );
      }
    }
  }

  // Render children if all checks pass
  return <>{children}</>;
};
